import {
    closestCenter,
    DndContext,
    type DragEndEvent,
    DragOverlay,
    type DragStartEvent,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core'
import { arrayMove, rectSortingStrategy, SortableContext, useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { type ComponentType, type CSSProperties, useState } from 'react'

import type { DashboardCardDef } from '../../../../../common/dashboard-cards.js'
import { t } from '../../../../../common/i18n.js'
import type { Language } from '../../../../../common/types/i18n.js'
import { Plus } from '../../../../components/icon/solar-icons.js'
import { CardFrame } from './card-frame.js'
import { CARD_VISUALS } from './cards/index.js'
import { lookupI18n } from './i18n-lookup.js'
import type { LayoutCard } from './layout-utils.js'

export type CardPayload = unknown

type CardComponentProps = { data: unknown; lang: Language; isMobile: boolean }

interface Props {
    lang: Language
    isMobile: boolean
    layout: LayoutCard[]
    payload: Record<string, CardPayload>
    registry: Record<string, DashboardCardDef>
    cardComponents: Record<string, ComponentType<CardComponentProps>>
    editMode: boolean
    onReorder: (keys: string[]) => void
    onRemove: (cardKey: string) => void
    onResize: (cardKey: string, patch: { colSpan?: number; rowSpan?: number }) => void
    onRetryCard: (cardKey: string) => void
    onOpenPicker: () => void
    /**
     * 'mandatory' renders the dark "black box" variant: no delete control on
     * cards and no "add card" tile. Defaults to 'regular'.
     */
    variant?: 'regular' | 'mandatory'
}

export const DashboardGrid = ({
    lang,
    isMobile,
    layout,
    payload,
    registry,
    cardComponents,
    editMode,
    onReorder,
    onRemove,
    onResize,
    onRetryCard,
    onOpenPicker,
    variant = 'regular',
}: Props) => {
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }))
    const [activeId, setActiveId] = useState<string | null>(null)

    if (layout.length === 0 && !editMode) return null

    const keys = layout.map((c) => c.key)

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={(e: DragStartEvent) => setActiveId(String(e.active.id))}
            onDragEnd={(e: DragEndEvent) => {
                setActiveId(null)
                if (!e.over || e.active.id === e.over.id) return
                const oldIndex = keys.indexOf(String(e.active.id))
                const newIndex = keys.indexOf(String(e.over.id))
                if (oldIndex < 0 || newIndex < 0) return
                onReorder(arrayMove(keys, oldIndex, newIndex))
            }}
            onDragCancel={() => setActiveId(null)}
        >
            <SortableContext items={keys} strategy={rectSortingStrategy}>
                <div
                    className={
                        variant === 'mandatory'
                            ? 'portal-dashboard__bento portal-dashboard__bento--mandatory'
                            : 'portal-dashboard__bento'
                    }
                >
                    {layout.map((card) => (
                        <SortableCardWrapper
                            key={card.key}
                            card={card}
                            registry={registry}
                            cardComponents={cardComponents}
                            payload={payload}
                            editMode={editMode}
                            isMobile={isMobile}
                            canRemove={variant === 'regular'}
                            canResize={variant === 'regular'}
                            onRemove={onRemove}
                            onResize={onResize}
                            onRetryCard={onRetryCard}
                            lang={lang}
                        />
                    ))}
                    {editMode && variant === 'regular' && (
                        <button
                            type="button"
                            className="portal-dashboard__add-tile"
                            onClick={onOpenPicker}
                            aria-label={t.portal.dashboard.edit.addCard(lang)}
                        >
                            <Plus size={16} />
                            <span>{t.portal.dashboard.edit.addCard(lang)}</span>
                        </button>
                    )}
                </div>
            </SortableContext>
            <DragOverlay>
                {activeId ? (
                    <DragOverlayCard
                        cardKey={activeId}
                        registry={registry}
                        cardComponents={cardComponents}
                        payload={payload}
                        lang={lang}
                        isMobile={isMobile}
                    />
                ) : null}
            </DragOverlay>
        </DndContext>
    )
}

interface DragOverlayCardProps {
    cardKey: string
    registry: Record<string, DashboardCardDef>
    cardComponents: Record<string, ComponentType<CardComponentProps>>
    payload: Record<string, CardPayload>
    lang: Language
    isMobile: boolean
}

const DragOverlayCard = ({
    cardKey,
    registry,
    cardComponents,
    payload,
    lang,
    isMobile,
}: DragOverlayCardProps) => {
    const def = registry[cardKey]
    if (!def) return null

    const Comp = cardComponents[cardKey]
    const data = payload[cardKey]

    const state: 'idle' | 'loading' | 'error' =
        data === undefined
            ? 'loading'
            : data && typeof data === 'object' && 'error' in data
              ? 'error'
              : 'idle'
    const visuals = CARD_VISUALS[cardKey as keyof typeof CARD_VISUALS]
    const action = state === 'idle' ? (visuals?.action?.(data, lang) ?? null) : null

    return (
        <div className="portal-dashboard__drag-overlay">
            <CardFrame
                lang={lang}
                title={lookupI18n(def.titleI18n, lang)}
                icon={visuals?.icon}
                action={action}
                state={state}
                editMode={false}
            >
                {state === 'idle' && Comp ? (
                    <Comp data={data} lang={lang} isMobile={isMobile} />
                ) : null}
            </CardFrame>
        </div>
    )
}

interface SortableCardWrapperProps {
    card: LayoutCard
    registry: Record<string, DashboardCardDef>
    cardComponents: Record<string, ComponentType<CardComponentProps>>
    payload: Record<string, CardPayload>
    editMode: boolean
    isMobile: boolean
    /** When false (mandatory cards), the delete control is hidden. */
    canRemove: boolean
    /** When false (mandatory cards), the resize control is hidden. */
    canResize: boolean
    onRemove: (key: string) => void
    onResize: (key: string, patch: { colSpan?: number; rowSpan?: number }) => void
    onRetryCard: (key: string) => void
    lang: Language
}

const SortableCardWrapper = ({
    card,
    registry,
    cardComponents,
    payload,
    editMode,
    isMobile,
    canRemove,
    canResize,
    onRemove,
    onResize,
    onRetryCard,
    lang,
}: SortableCardWrapperProps) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: card.key,
        disabled: !editMode,
    })

    const def = registry[card.key]
    if (!def) return null

    const Comp = cardComponents[card.key]
    const data = payload[card.key]

    const state: 'idle' | 'loading' | 'error' =
        data === undefined
            ? 'loading'
            : data && typeof data === 'object' && 'error' in data
              ? 'error'
              : 'idle'
    const visuals = CARD_VISUALS[card.key as keyof typeof CARD_VISUALS]
    const action = state === 'idle' ? (visuals?.action?.(data, lang) ?? null) : null

    // On mobile the grid collapses to a single column, so every card spans the
    // full width; row span still applies so taller content keeps its height.
    const style: CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.35 : 1,
        gridColumn: isMobile ? '1 / -1' : `span ${card.colSpan}`,
        gridRow: `span ${card.rowSpan}`,
        // Grid items default to min-width: auto and refuse to shrink below their
        // content's intrinsic width (a chart, long email/URL), which pushes the
        // whole grid past the viewport and causes horizontal scroll on mobile.
        minWidth: 0,
    }

    return (
        <div ref={setNodeRef} style={style}>
            <CardFrame
                lang={lang}
                title={lookupI18n(def.titleI18n, lang)}
                icon={visuals?.icon}
                action={action}
                state={state}
                editMode={editMode}
                colSpan={card.colSpan}
                rowSpan={card.rowSpan}
                onRemove={canRemove ? () => onRemove(card.key) : undefined}
                onResize={canResize ? (patch) => onResize(card.key, patch) : undefined}
                onRetry={() => onRetryCard(card.key)}
                dragHandleProps={{ ...attributes, ...listeners }}
            >
                {state === 'idle' && Comp ? (
                    <Comp data={data} lang={lang} isMobile={isMobile} />
                ) : null}
            </CardFrame>
        </div>
    )
}
